/* eslint-disable @typescript-eslint/no-explicit-any */
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WA_WINDOW } from '@ng-web-apis/common';
import { PublicUserContextService } from '@rma/public/accounts/user';
import { DynamicAssetLoaderService, DynamicLoaderAssets } from '@rma/public/generic/util/dynamic-asset-loader';
import { Environment } from '@rma/public/generic/util/environment';
import { FeatureFlags, FeatureFlagService } from '@rma/public/generic/util/feature-flags';
import { filter, ReplaySubject, startWith, switchMap, take, withLatestFrom } from 'rxjs';
import { ChatContextService } from '../data-access/chat-context.service';

declare global {
  interface Window {
    intercomSettings?: any;
  }
  function Intercom(value: string, params?: any): void;
}

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  private readonly chatApiService = inject(ChatContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dynamicAssetLoader = inject(DynamicAssetLoaderService);
  private readonly featureProvider = inject(FeatureFlagService);
  private readonly userService = inject(PublicUserContextService);
  private readonly window = inject(WA_WINDOW);
  private readonly env = inject(Environment);

  private readonly isEnabled$ = this.featureProvider.getFeature$(FeatureFlags.EnableIntercom, false).pipe(filter((x) => x));

  private readonly chatData$ = this.chatApiService.userChatData$.pipe(
    take(1),
    filter((x) => !!x['app_id']),
  );

  private readonly isLoaded = new ReplaySubject<boolean>(1);
  public readonly isLoaded$ = this.isLoaded.asObservable().pipe(startWith(false));

  public initialise(): void {
    this.isEnabled$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.userService.currentUser$),
        filter((x) => x !== null && !x.isImpersonating),
        switchMap(() => this.chatData$),
        take(1),
      )
      .subscribe((x) => this.loadIntercom(x));
  }

  public initaliseWithoutCurrentUser(): void {
    // As this is not prevented for impersonated user, we don't want this triggering in dev
    if (this.env.type === 'prod') {
      this.isEnabled$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          withLatestFrom(this.isLoaded$),
          filter(([_, x]) => !x),
          switchMap(() => this.chatData$),
          take(1),
        )
        .subscribe((x) => this.loadIntercom(x));
    }
  }

  public showMessenger(): void {
    Intercom('show');
  }

  private loadIntercom(x: Record<string, unknown>) {
    this.window.intercomSettings = x;
    this.dynamicAssetLoader.load(DynamicLoaderAssets.Intercom).subscribe((y) => this.isLoaded.next(y));
  }
}
